import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import { AppActionCreators } from '@nkr/store/index';
import { ActionCreators as AuthActionCreators } from '@nkr/store/authorization/actions';
import { toast } from 'react-toastify';
import { Fetcher } from './fetcher';
import { useEffect, useMemo, useRef } from 'react';
import { useAppHookContext } from './useAppHookContext';
import type { IRestModel, IRestResponse, RestApiHooks, SearchParams, JsonInit } from './types';

function isObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null;
}
function hasResponse<T>(e: unknown): e is { response: IRestResponse<T> } {
    return isObject(e) && 'response' in e && isObject((e as { response: unknown }).response);
}
function hasMessage(e: unknown): e is { message?: string } {
    return isObject(e) && 'message' in e;
}
function omitId<T extends IRestModel>(o: T): Omit<T, 'id'> {
    // Shallow copy, then remove `id`
    const copy = { ...o } as Omit<T, 'id'> & { id?: unknown };
    delete (copy as Record<'id', unknown>).id;
    return copy as Omit<T, 'id'>;
}

type SaveInput<T extends IRestModel>     = T | Omit<T, 'id'>;
type LoadMapper<T extends IRestModel>    = (json: Partial<T> & Record<string, unknown>) => T;
type ErrorMessages<T extends IRestModel> = Record<keyof T & string, string> | null;

export class RestApi<TListType extends IRestModel, TDetailType extends IRestModel> {
    private routeParams    : Record<string, string | number | boolean | null | undefined> = {};
    private listAllPath    : string;
    private searchPath     : string;
    private createPath     : string;
    private getByIdPath    : string;
    private updatePath     : string;
    private deletePath     : string;
    public  fetcher        : Fetcher;

    // Mappers accept spreadable "raw" shapes; outputs are concrete
    private listMapper    ?: LoadMapper<TListType>;
    private loadMapper    ?: LoadMapper<TDetailType>;
    private saveMapper    ?: (entity: SaveInput<TDetailType>) => SaveInput<TDetailType> | unknown;

    constructor(baseUrl: string) {
        this.fetcher        = new Fetcher();
        this.listAllPath    = baseUrl;
        this.searchPath     = `${baseUrl}`;
        this.createPath     = `${baseUrl}`;
        this.getByIdPath    = `${baseUrl}/{id}`;
        this.updatePath     = `${baseUrl}/{id}`;
        this.deletePath     = `${baseUrl}/{id}`;
    }

    static createApi<TS extends IRestModel, TD extends IRestModel>(baseUrl: string) {
        return new RestApi<TS, TD>(baseUrl);
    }

    withCustomSearchPath(path: string): this {
        this.searchPath = path;
        return this;
    }
    withCustomCreatePath(path: string): this {
        this.createPath = path;
        return this;
    }
    withCustomGetByIdPath(path: string): this {
        this.getByIdPath = path;
        return this;
    }
    withCustomUpdatePath(path: string): this {
        this.updatePath = path;
        return this;
    }
    withCustomDeletePath(path: string): this {
        this.deletePath = path;
        return this;
    }

    withListMapper( fn: LoadMapper<TListType> ): this {
        this.listMapper = fn;
        return this;
    }
    withLoadMapper( fn: LoadMapper<TDetailType> ): this {
        this.loadMapper = fn;
        return this;
    }
    withSaveMapper(fn: (entity: SaveInput<TDetailType>) => SaveInput<TDetailType> | unknown): this {
        this.saveMapper = fn;
        return this;
    }

    setRouteParams( params: Record<string, string | number | boolean | null | undefined> ): this {
        this.routeParams = { ...this.routeParams, ...params };
        return this;
    }

    private normalizePayload = (resp?: IRestResponse<TDetailType> | any): IRestResponse<TDetailType> | undefined => {
        const isSuccess : boolean                       = resp && (resp?.success === true || resp?.isSuccess === true);
        const results   : TDetailType | null            = resp?.results ?? resp?.result ?? resp?.payload ?? resp?.data;
        const message   : string | null                 = resp?.message ?? null;
        const errors    : ErrorMessages<TDetailType>    = resp?.errors ?? null;
        const payload   : TDetailType | undefined       = (results && this.loadMapper)
                                                            ? this.loadMapper(results as Partial<TDetailType> & Record<string, unknown>)
                                                            : (results as TDetailType | undefined);
        return { isSuccess, message, errors, payload } as IRestResponse<TDetailType> | undefined;
    };

    private normalizePayloadList = (resp?: IRestResponse<TListType[]> | any): IRestResponse<TListType[]> | undefined => {
        const isSuccess : boolean                       = resp && (resp?.success === true || resp?.isSuccess === true);
        const results   : TDetailType | null            = resp?.results ?? resp?.result ?? resp?.payload ?? resp?.data;
        const message   : string | null                 = resp?.message ?? null;
        const errors    : ErrorMessages<TDetailType>    = resp?.errors ?? null;
        let list                                        = Array.isArray(results) ? (results as any[]) : [];
        if (this.listMapper) {
            list = list.map(item => this.listMapper(item as Partial<TListType> & Record<string, unknown>));
        }
        const payload   : TListType[] | undefined       = list;
        return { isSuccess, message, errors, payload } as IRestResponse<TListType[]> | undefined;
    };

    private applySave(entity: SaveInput<TDetailType>): unknown {
        return this.saveMapper ? this.saveMapper(entity) : entity;
    }

    public getInterpolatedPath(path: string, id: string | number): string {
        Object.entries(this.routeParams).forEach(([k, v]) => {
            const toReplace = `{${k}}`;
            path = path.replace(toReplace, v != null ? encodeURIComponent(String(v)) : '');
        });
        
        return path.replace('{id}', encodeURIComponent(String(id)));
    }
    /* ----------------- SWR cache helpers ----------------- */

    private refreshList(showSpinner: (isVisible: boolean, message: string) => void = () => {}) {
        showSpinner(true, 'Refreshing list...');
        const listSwrKey = this.getInterpolatedPath(this.listAllPath, 0);
        mutate(listSwrKey, undefined, { revalidate: true }).finally(() => showSpinner(false, 'Refreshing list...'));
    }

    private refreshOptimistic( updated: IRestResponse<TDetailType> ) {
        const entityId = updated?.payload?.id;
        if (entityId != null) {
            const entityUrl = this.getInterpolatedPath(this.getByIdPath, entityId);

            mutate(
                entityUrl,
                updated,
                false
            );
        }

        this.refreshList();
    }

    private removeFromCache(id: string | number) {
        mutate(this.getInterpolatedPath(this.getByIdPath,id), undefined, false);
        this.refreshList();
    }

    /**
     * Seeds the SWR cache for a single entity with a payload obtained outside the
     * normal create/update hooks (e.g. a follow-up file upload call), so a later
     * useGetById(id) doesn't serve the stale pre-upload response.
     */
    public refreshCachedEntity(payload: TDetailType): void {
        this.refreshOptimistic({ isSuccess: true, message: null, errors: null, payload });
    }

    /* -------------------- Hook factory -------------------- */

    private _hooks?: RestApiHooks<TListType, TDetailType>;

    public makeHooks(): RestApiHooks<TListType, TDetailType> {
        if (this._hooks) return this._hooks;

        const makeAuthedFetcher = (dispatch: Function) => async <R,>(url: string) => {
            try {
                const auth      = await dispatch(AuthActionCreators.checkToken());
                const token     = (auth && (auth.token && (auth.token as any).access_token)) as string | undefined;
                const headers: HeadersInit = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                } as any;
                if (token) (headers as any)['Authorization'] = `Bearer ${token}`;
                return this.fetcher.fetch<R>(url, { headers });
            }
            catch (e) {
                return this.fetcher.fetch<R>(url);
            }
        };

        const makeAuthedMutationFetcher = (dispatch: Function) => async <R,>(key: string, { arg }: { arg: JsonInit }) => {
            try {
                const auth      = await dispatch(AuthActionCreators.checkToken());
                const token     = (auth && (auth.token && (auth.token as any).access_token)) as string | undefined;
                const mergedHeaders: Record<string, string> = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(arg?.headers as any || {}),
                };
                if (token) mergedHeaders['Authorization'] = `Bearer ${token}`;

                return this.fetcher.fetchWithMutation<R>(key, { arg: { ...arg, headers: mergedHeaders } });
            }
            catch (e) {
                return this.fetcher.fetchWithMutation<R>(key, { arg });
            }
        };
        const useFetch = <R,>(
            url: string | null,
            mapFn: (resp?: IRestResponse<R>) => IRestResponse<R> | undefined,
            labels: { loading: string; fail: string }
        ): IRestResponse<R> | undefined => {
            const { dispatch, centerId, isReady } = useAppHookContext();

            const doApiCall = isReady && !/\{\w+\}/.test(url);
            const swrKey    = doApiCall ? url : null;

            const { data, error, isLoading, isValidating } = useSWR<IRestResponse<R>>(swrKey, makeAuthedFetcher(dispatch), {
                // There's probably a balance to find here somewhere. It was refreshing too often without
                // the below configuration, but this may not be enough refreshing...?
                revalidateOnFocus       : false,
                revalidateIfStale       : false,
                revalidateOnReconnect   : true,
            });

            // Show spinner only when we actually attempt a fetch (isReady) and key is non-null.
            const showSpinner       = doApiCall && (isLoading || (isValidating && !data && !error));
            const wasShowingRef     = useRef(false);
            useEffect(() => {
                if (showSpinner && !wasShowingRef.current) {
                    dispatch(AppActionCreators.showLoadingAsync(labels.loading));
                    wasShowingRef.current = true;
                    return;
                }
                if (!showSpinner && wasShowingRef.current) {
                    dispatch(AppActionCreators.hideLoading());
                    wasShowingRef.current = false;
                }
            }, [showSpinner, labels.loading, dispatch]);

            // While visible, update message if it changes
            useEffect(() => {
                if (wasShowingRef.current && showSpinner) {
                    dispatch(AppActionCreators.showLoadingAsync(labels.loading));
                }
            }, [labels.loading, showSpinner, dispatch]);

            useEffect(() => {
                return () => {
                    if (wasShowingRef.current) {
                        dispatch(AppActionCreators.hideLoading());
                        wasShowingRef.current = false;
                    }
                };
            }, [dispatch]);

            useEffect(() => {
                if (!error) return;
                const msg = hasMessage(error) && error.message ? error.message : 'Network error';
                toast.error(msg);
            }, [error]);

            useEffect(() => {
                if (!data || data.isSuccess !== false) return;
                toast.error(data.message || labels.fail);
            }, [data, labels.fail]);

            // Derive source only if we have attempted a request (isReady)
            const source: IRestResponse<R> | undefined =
                hasResponse<R>(error) ? (error as { response: IRestResponse<R> }).response : data;

            return useMemo(() => mapFn(source), [source, mapFn]);
        };

        const useGetAll = (): IRestResponse<TListType[]> | undefined => {
            const { centerId } = useAppHookContext();

            if( centerId != null && this.routeParams['centerId'] != centerId ) {
                this.setRouteParams({ centerId });
            }

            return useFetch<TListType[]>(
                this.getInterpolatedPath(this.listAllPath, 0),
                this.normalizePayloadList,
                { loading: 'Loading...', fail: 'Fetch failed' }
            );
        }

        const useGetById = (id: string | number | null): IRestResponse<TDetailType> | undefined => {
            const { centerId } = useAppHookContext();

            if( centerId != null && this.routeParams['centerId'] != centerId ) {
                this.setRouteParams({ centerId });
            }

            const url = id != null ? this.getInterpolatedPath(this.getByIdPath, id) : null;
            return useFetch<TDetailType>(
                url,
                this.normalizePayload,
                { loading: 'Loading...', fail: 'Fetch failed' }
            );
        };

        const useCreate = () => {
            const { dispatch, centerId } = useAppHookContext();

            if( centerId != null && this.routeParams['centerId'] != centerId ) {
                this.setRouteParams({ centerId });
            }

            const { trigger, data, error, isMutating } =
                useSWRMutation<IRestResponse<TDetailType>, Error, string, JsonInit>(
                    this.getInterpolatedPath(this.createPath, 0),
                    makeAuthedMutationFetcher(dispatch)
                );

            const submit = async (payload: TDetailType): Promise<IRestResponse<TDetailType>> => {
                dispatch(AppActionCreators.showLoadingAsync('Saving...'));
                try {
                    const body      = this.applySave(omitId(payload));
                    const resp      = await trigger({ method: 'POST', body } as JsonInit);
                    const result    = this.normalizePayload(resp);

                    if (result?.isSuccess) {
                        toast.success('Save successful');

                        this.refreshOptimistic(result);
                    }
                    else {
                        toast.error(result?.message || 'Save failed');
                    }
                    return result;
                }
                finally {
                    dispatch(AppActionCreators.hideLoading());
                }
            };

            return { submit, response: this.normalizePayload(data), error, isMutating };
        };

        const useUpdate = (id: string | number) => {
            const entityUrl = this.getInterpolatedPath(this.updatePath, id);
            
            const { dispatch, centerId } = useAppHookContext();

            if( centerId != null && this.routeParams['centerId'] != centerId ) {
                this.setRouteParams({ centerId });
            }

            const { trigger, data, error, isMutating } =
            
                useSWRMutation<IRestResponse<TDetailType>, Error, string, JsonInit>(
                    entityUrl,
                    makeAuthedMutationFetcher(dispatch)
                );

            const submit = async (payload: TDetailType): Promise<IRestResponse<TDetailType>> => {
                dispatch(AppActionCreators.showLoadingAsync('Saving...'));
                try {
                    const body   = this.applySave(payload);
                    const resp   = await trigger({ method: 'PUT', body } as JsonInit);
                    const result = this.normalizePayload(resp);
                    
                    if( result.isSuccess ) {
                        toast.success('Update successful');
                        this.refreshOptimistic(result);
                    }
                    else {
                        toast.error(result?.message || 'Update failed');
                    }

                    return result;
                }
                finally {
                    dispatch(AppActionCreators.hideLoading());
                }
            };

            return { submit, response: this.normalizePayload(data), error, isMutating };
        };

        const useDelete = (id: string | number) => {
            const { dispatch, centerId } = useAppHookContext();

            if( centerId != null && this.routeParams['centerId'] != centerId ) {
                this.setRouteParams({ centerId });
            }

            const { trigger, data, error, isMutating } =
                useSWRMutation<IRestResponse<TDetailType>, Error, string, JsonInit>(
                    this.getInterpolatedPath(this.deletePath, id),
                    makeAuthedMutationFetcher(dispatch)
                );

            const submit = async (): Promise<IRestResponse<TDetailType>> => {
                dispatch(AppActionCreators.showLoadingAsync('Deleting...'));

                try {
                    const resp      = await trigger({ method: 'DELETE' } as JsonInit);
                    const result    = this.normalizePayload(resp);

                    if (result?.isSuccess) {
                        toast.success('Delete successful');
                        this.removeFromCache(id);
                    }
                    else {
                        toast.error(result?.message || 'Delete failed');
                    }

                    return result;
                }
                finally {
                    dispatch(AppActionCreators.hideLoading());
                }
            };

            return { submit, response: this.normalizePayload(data), error, isMutating };
        };

        /**
         * useSearch doesn't use SWR to avoid caching too much data.
         */
        const useSearch = (params: SearchParams | null): IRestResponse<TListType[]> | undefined => {
            const { centerId } = useAppHookContext();

            if( centerId != null && this.routeParams['centerId'] != centerId ) {
                this.setRouteParams({ centerId });
            }

            const qs = params
                ? (() => {
                    const usp = new URLSearchParams();
                    for (const [k, v] of Object.entries(params)) {
                        if (Array.isArray(v)) {
                            for (const item of v) {
                                if (item !== undefined && item !== null) usp.append(k, String(item));
                            }
                        } else if (v !== undefined && v !== null) {
                            usp.append(k, String(v));
                        }
                    }
                    return usp.toString();
                })()
                : '';
            const url = params ? `${this.getInterpolatedPath(this.searchPath,0)}?${qs}` : null;

            return useFetch<TListType[]>(
                url,
                this.normalizePayloadList,
                { loading: 'Searching...', fail: 'Search failed' }
            );
        };

        return (this._hooks = {
            apiReference : this,
            useGetAll,
            useGetById,
            useSearch,
            useCreate,
            useUpdate,
            useDelete,
            refreshCachedEntity: this.refreshCachedEntity.bind(this),
        });
    }
}
